import {
  TOASTS,
  TOAST_TYPES,
  TOAST_CLASS,
  DEFAULT_OPTIONS,
} from '@/composables/toast/consts.js'

import { useEmitter } from '@/composables/emitter/index.js'

import { EVENTS } from '@/lib/consts.js'

import Toastify from 'toastify-js'
import 'toastify-js/src/toastify.css'
import { useLogger } from '@booksy-com/vue-tools'
import DOMPurify from 'dompurify'

const getClassName = (options) => [options?.className, TOAST_CLASS].filter(Boolean).join(' ')

const showToast = (type, text, options) => {
  text = transformText(text, options)
  Toastify({
    ...TOASTS[type],
    ...options,
    className: getClassName(options),
    text,
  })
    .showToast()
}

const error = (text, options) => {
  const emitter = useEmitter()
  emitter.emit(EVENTS.TOAST_ERROR, text)

  return showToast(TOAST_TYPES.ERROR, text, options)
}

const success = (text, options) => {
  return showToast(TOAST_TYPES.SUCCESS, text, options)
}

const info = (text, options) => {
  return showToast(TOAST_TYPES.INFO, text, options)
}

const warn = (text, options) => {
  return showToast(TOAST_TYPES.WARN, text, options)
}

const show = (text, options) => {
  return Toastify({
    ...DEFAULT_OPTIONS,
    ...options,
    className: getClassName(options),
    text,
  }).showToast()
}

const goAway = (toast) => {
  toast.hideToast()
}

const getDebugMessage = (text, options = {}) => {
  try {
    if (options.debugMessage instanceof Error) {
      // api error handling
      if (options.debugMessage.response) {
        const response = options.debugMessage.response

        if (response?.data.errors?.[0]?.description) {
          return [
            response?.data.errors?.[0]?.field,
            response?.data.errors?.[0]?.code,
            response?.data.errors?.[0]?.description,
          ].filter(v => !!v).join(' | ')
        }

        if (response?.data.error) {
          return response?.data.error
        }

        if (response?.data.message) {
          return response?.data.message
        }
      }

      // js standard error
      return options.debugMessage.message
    }

    return options.debugMessage
  } catch (e) {
    const logger = useLogger('toast')
    logger.error(e)
    return null
  }
}

const transformText = (text, options) => {
  const DEBUG_MESSAGE_LENGTH = 100

  let debugMessage = getDebugMessage(text, options)

  if (!debugMessage || debugMessage === text) {
    return DOMPurify.sanitize(text, { RETURN_DOM: true }).innerHTML
  }

  debugMessage = debugMessage.length > DEBUG_MESSAGE_LENGTH
    ? debugMessage.substring(0, DEBUG_MESSAGE_LENGTH) + '...'
    : debugMessage

  // nosemgrep: html-in-template-string
  return `${DOMPurify.sanitize(text, { RETURN_DOM: true }).innerHTML} <div class="toastedDebugMessage">${debugMessage}</div>`
}

export const useToast = () => {
  return {
    success,
    error,
    info,
    show,
    goAway,
    warn,
  }
}
